import { assetUrl } from '~/utils/assetUrl'

/**
 * Creature art registry. Source files live in `art/` (repo root); the
 * web-ready copies go in `public/creatures/<defId>.webp` and get listed
 * here (a registry beats probing so we never spray 404s). Creatures
 * without art fall back to their avatar.
 *
 * ☁️ v7.84: the registries below hold SITE PATHS (the bucket keys of the
 * art host); the functions hand out the URLs to render, through assetUrl().
 */
export const CREATURE_ART: Record<string, string> = {
  // 🎨 v3.25 expansion batch (per Javi's approved sources): stills
  // installed for every state; animations replace them as they render.
  'aeon-sprout': '/creatures/aeon-sprout.webp',
  'ashen-vicar': '/creatures/ashen-vicar.webp',
  'aurora-serpent': '/creatures/aurora-serpent.webp',
  'barrow-knight': '/creatures/barrow-knight.webp',
  'barrowfur-bear': '/creatures/barrowfur-bear.webp',
  'cinderowl': '/creatures/cinderowl.webp',
  'coalpip': '/creatures/coalpip.webp',
  'deepcurrent-oracle': '/creatures/deepcurrent-oracle.webp',
  'dewdrop-axolotl': '/creatures/dewdrop-axolotl.webp',
  'dunemite': '/creatures/dunemite.webp',
  'duskmoth-empress': '/creatures/duskmoth-empress.webp',
  'emberlord-kirin': '/creatures/emberlord-kirin.webp',
  legacy: '/creatures/legacy.webp',
  // 🎗️ v6.56: the homage creatures (Javi's five sources, keyed), stills
  // in every slot; v6.57 replaced each slot with its Ludo loop under the
  // same name (b=2 / r=6 / m=2 below bust the day-long cache). Padded
  // anchors live in _anchors/. No defs yet: the kits are Javi's to
  // decide, so nothing lists or deals them.
  'mr-khool': '/creatures/mr-khool.webp',
  aid: '/creatures/aid.webp',
  lanfeust: '/creatures/lanfeust.webp',
  robodude: '/creatures/robodude.webp',
  'the-noble-one': '/creatures/the-noble-one.webp',
  'fernkit': '/creatures/fernkit.webp',
  'flarelet': '/creatures/flarelet.webp',
  'forge-calf': '/creatures/forge-calf.webp',
  'ghost-lantern': '/creatures/ghost-lantern.webp',
  'glacier-warden': '/creatures/glacier-warden.webp',
  'gloomcap': '/creatures/gloomcap.webp',
  'hoardwyrm': '/creatures/hoardwyrm.webp',
  'hollow-sage': '/creatures/hollow-sage.webp',
  'hollowbark-stag': '/creatures/hollowbark-stag.webp',
  'ironvine-colossus': '/creatures/ironvine-colossus.webp',
  'kilnwyrm': '/creatures/kilnwyrm.webp',
  'loamgrub': '/creatures/loamgrub.webp',
  'loamshell-crab': '/creatures/loamshell-crab.webp',
  'marrowlight-jelly': '/creatures/marrowlight-jelly.webp',
  'mirror-mime': '/creatures/mirror-mime.webp',
  'mirrorfin': '/creatures/mirrorfin.webp',
  'mosskit': '/creatures/mosskit.webp',
  'mosswick': '/creatures/mosswick.webp',
  'naptoad': '/creatures/naptoad.webp',
  'nightbloom': '/creatures/nightbloom.webp',
  'ouroboros': '/creatures/ouroboros.webp',
  'pebblet': '/creatures/pebblet.webp',
  'prismarine-eel': '/creatures/prismarine-eel.webp',
  'pyrelord-ossuary': '/creatures/pyrelord-ossuary.webp',
  'pyrepup': '/creatures/pyrepup.webp',
  'quillhog': '/creatures/quillhog.webp',
  'rillhopper': '/creatures/rillhopper.webp',
  'rootwarden': '/creatures/rootwarden.webp',
  'runestone-sentinel': '/creatures/runestone-sentinel.webp',
  'sable-corsair': '/creatures/sable-corsair.webp',
  'silt-colossus': '/creatures/silt-colossus.webp',
  'slagbeast': '/creatures/slagbeast.webp',
  'solar-kirin': '/creatures/solar-kirin.webp',
  'solstice-phoenix': '/creatures/solstice-phoenix.webp',
  'sparkvein-hare': '/creatures/sparkvein-hare.webp',
  'springtail': '/creatures/springtail.webp',
  'stormforge-smith': '/creatures/stormforge-smith.webp',
  'stormsinger': '/creatures/stormsinger.webp',
  'sunfledge': '/creatures/sunfledge.webp',
  'thistlemote': '/creatures/thistlemote.webp',
  'thornheart-lily': '/creatures/thornheart-lily.webp',
  'tidemother-leviathan': '/creatures/tidemother-leviathan.webp',
  'wispkeeper': '/creatures/wispkeeper.webp',
  'ember-cub': '/creatures/ember-cub.webp',
  'marshfang': '/creatures/marshfang.webp',
  'cinderfang': '/creatures/cinderfang.webp',
  'cinder-moth': '/creatures/cinder-moth.webp',
  'misthorn': '/creatures/misthorn.webp',
  'bogmaw': '/creatures/bogmaw.webp',
  'sparkmouse': '/creatures/sparkmouse.webp',
  'root-toad': '/creatures/root-toad.webp',
  'bramblehorn': '/creatures/bramblehorn.webp',
  'pebble-pup': '/creatures/pebble-pup.webp',
  'sproutling': '/creatures/sproutling.webp',
  'stoneback': '/creatures/stoneback.webp',
  'stormclaw': '/creatures/stormclaw.webp',
  'prism-fox': '/creatures/prism-fox.webp',
  'voltgnaw': '/creatures/voltgnaw.webp',
  'pyre-hound': '/creatures/pyre-hound.webp',
  'ashwing': '/creatures/ashwing.webp',
  'volt-finch': '/creatures/volt-finch.webp',
  'terra-tort': '/creatures/terra-tort.webp',
  'storm-kit': '/creatures/storm-kit.webp',
  'soot-sprite': '/creatures/soot-sprite.webp',
  'ripwing': '/creatures/ripwing.webp',
  'pepper-sprout': '/creatures/pepper-sprout.webp',
  'mudfin': '/creatures/mudfin.webp',
  'grove-snail': '/creatures/grove-snail.webp',
  'brook-seal': '/creatures/brook-seal.webp',
  'lava-newt': '/creatures/lava-newt.webp',
  'gale-koi': '/creatures/gale-koi.webp',
  'marsh-wisp': '/creatures/marsh-wisp.webp',
  'steam-toad': '/creatures/steam-toad.webp',
  'dew-hare': '/creatures/dew-hare.webp',
  'bubble-bat': '/creatures/bubble-bat.webp',
  'reed-otter': '/creatures/reed-otter.webp',
  'glow-moss': '/creatures/glow-moss.webp',
  // 🎨 v3.54: the last twelve sources Javi owed the expansion. Stills
  // for now — they are listed in STATE_PENDING below until their run,
  // victory and defeat loops render, so nothing asks for a missing file.
  'cinderquill': '/creatures/cinderquill.webp',
  'gustfin': '/creatures/gustfin.webp',
  'gildmaw': '/creatures/gildmaw.webp',
  'karaoke-kraken': '/creatures/karaoke-kraken.webp',
  'mirroring': '/creatures/mirroring.webp',
  'gilt-minnow': '/creatures/gilt-minnow.webp',
  'cragjaw': '/creatures/cragjaw.webp',
  'gigglemoth': '/creatures/gigglemoth.webp',
  'foehn-sylph': '/creatures/foehn-sylph.webp',
  'ember-reliquary': '/creatures/ember-reliquary.webp',
  'leviathan-calf': '/creatures/leviathan-calf.webp',
  'mirricrow': '/creatures/mirricrow.webp',
}

/** 🏛️ Showcase dioramas (v1.03): the creature standing on the element
 * pedestal (tools/pedestal_composite.py). Shown by the shop inspector;
 * everything else keeps the transparent close-up above. */
export const CREATURE_SHOWCASE: Record<string, string> = {
  'marshfang': '/creatures/marshfang-showcase.webp',
  'cinderfang': '/creatures/cinderfang-showcase.webp',
  'cinder-moth': '/creatures/cinder-moth-showcase.webp',
  'misthorn': '/creatures/misthorn-showcase.webp',
  'bogmaw': '/creatures/bogmaw-showcase.webp',
  'sparkmouse': '/creatures/sparkmouse-showcase.webp',
  'root-toad': '/creatures/root-toad-showcase.webp',
  'bramblehorn': '/creatures/bramblehorn-showcase.webp',
  'pebble-pup': '/creatures/pebble-pup-showcase.webp',
  'sproutling': '/creatures/sproutling-showcase.webp',
  'stoneback': '/creatures/stoneback-showcase.webp',
  'stormclaw': '/creatures/stormclaw-showcase.webp',
  'ember-cub': '/creatures/ember-cub-showcase.webp',
  'prism-fox': '/creatures/prism-fox-showcase.webp',
  'voltgnaw': '/creatures/voltgnaw-showcase.webp',
  'pyre-hound': '/creatures/pyre-hound-showcase.webp',
  'ashwing': '/creatures/ashwing-showcase.webp',
  'volt-finch': '/creatures/volt-finch-showcase.webp',
  'terra-tort': '/creatures/terra-tort-showcase.webp',
  'storm-kit': '/creatures/storm-kit-showcase.webp',
  'soot-sprite': '/creatures/soot-sprite-showcase.webp',
  'ripwing': '/creatures/ripwing-showcase.webp',
  'pepper-sprout': '/creatures/pepper-sprout-showcase.webp?v=2', // 🌶️ v7.76: the new source on its pedestal
  'mudfin': '/creatures/mudfin-showcase.webp',
  'grove-snail': '/creatures/grove-snail-showcase.webp',
  'brook-seal': '/creatures/brook-seal-showcase.webp',
  'lava-newt': '/creatures/lava-newt-showcase.webp',
  'gale-koi': '/creatures/gale-koi-showcase.webp',
  'marsh-wisp': '/creatures/marsh-wisp-showcase.webp',
  'steam-toad': '/creatures/steam-toad-showcase.webp',
  'dew-hare': '/creatures/dew-hare-showcase.webp',
  'bubble-bat': '/creatures/bubble-bat-showcase.webp',
  'reed-otter': '/creatures/reed-otter-showcase.webp',
  'glow-moss': '/creatures/glow-moss-showcase.webp',
}

/** 🍃 v4.90 asset diet (perf review #1): every animated loop was
 * re-encoded smaller IN PLACE (idle 640→384px, victory/defeat →256px,
 * showcases 900→512px) — `d=1` busts the day-long cache so returning
 * players fetch the light files instead of replaying the heavy ones.
 * Bump on any future in-place re-encode of these classes. */
const DIET = 'd=1'
const diet = (url: string) => `${url}${url.includes('?') ? '&' : '?'}${DIET}`

/** 🔄 v5.80: per-id cache bust for BASE art replaced in place (the
 * idle loop + poster live under the same stem). Born for the Stormglass
 * Ibex, whose files overwrite the old hare art wholesale. */
export const BASE_REV_OVERRIDES: Record<string, number> = {
  'sparkvein-hare': 2,
  legacy: 2,
  // 🎗️ v6.57: the homage idles replaced their v6.56 stills in place.
  'mr-khool': 2,
  'aid': 2,
  'lanfeust': 2,
  'robodude': 2,
  'the-noble-one': 2,
  // 🎬 v7.11: idles (and their posters) re-rendered from padded anchors after Javi's /anim review.
  'dew-hare': 2,
  'bubble-bat': 2,
  'solstice-phoenix': 2,
  'sunfledge': 2,
  'tidemother-leviathan': 2,
  'springtail': 3, // v7.12 pose-locked idle; v7.25: Javi's new source painting, all four loops re-rendered on hydra
  // 🦋 v7.75 (per Javi: "Cindermoth idle animation slightly clips"): its wings ran off both sides of the tile in
  // every render since v1.19 - idle re-rendered on hydra off a padded anchor; the poster is its first frame.
  'cinder-moth': 2,
  // 🌶️ v7.76 (per Javi: a new Pepper Sprout source - upright on two legs now): every loop re-rendered on hydra.
  'pepper-sprout': 2,
}
const baseRev = (defId: string, url: string) => {
  const b = BASE_REV_OVERRIDES[defId]
  return b ? `${url}?b=${b}` : url
}

export function creatureArt(defId: string): string | null {
  const art = CREATURE_ART[defId]
  return art ? assetUrl(diet(baseRev(defId, art))) : null
}

/** Static first-frame of the close-up (v1.20): used in LIST views so a
 * screen full of creatures doesn't animate all at once — only the
 * inspected creature plays. `<id>-poster.webp` sits beside the close-up. */
export function creaturePoster(defId: string): string | null {
  const art = CREATURE_ART[defId]
  return art ? assetUrl(baseRev(defId, art.replace(/\.webp$/, '-poster.webp'))) : null
}

/** 🎭 v7.56: the four portrait moods of the face-off scenes (calm, determined,
 * hurt, angry): head-and-chest busts generated from Javi's paintings, one set
 * per creature in CREATURE_ART, under `/creatures/mood/`. No query string: the
 * files are new, so nothing is cached under these names (the art guard passes
 * bare paths, see shared/assetQuery.ts) - except a set redrawn in place, which
 * carries its MOOD_REV (`/creatures/**` is cached for a day). */
export const MOOD_FACES = ['calm', 'determined', 'hurt', 'angry'] as const
export type MoodFace = (typeof MOOD_FACES)[number]
/** 🎭 v7.71 (per Javi: "in the story mode, make sure all the creatures are facing to the right (root toad was
 * facing left)"): these four sets were painted facing left and are now mirrored in place - '2' = mirrored. */
export const MOOD_REV: Record<string, string> = {
  'root-toad': '2',
  'reed-otter': '2',
  'stormclaw': '2',
  'voltgnaw': '2',
}
export function creatureMood(defId: string, face: MoodFace): string | null {
  const art = CREATURE_ART[defId]
  if (!art) return null
  const base = art.slice(art.lastIndexOf('/') + 1).replace(/\.webp$/, '')
  const rev = MOOD_REV[base]
  return assetUrl(`/creatures/mood/${base}-${face}.webp${rev ? `?v=${rev}` : ''}`)
}
/** The moodFade.ts key for a portrait URL: its file name without the extension (or a rev). */
export function moodStem(url: string): string {
  return url.split('?')[0]!.split('/').pop()!.replace(/\.webp$/, '')
}

/* 🛡️ v7.41 (per Javi: "When inspecting the creature the idle animation
 * should play"): every inspect view shows the poster first and swaps to
 * the idle loop once it has decoded (components/CreatureIdle.vue). For
 * all but six creatures the poster IS the loop's first frame, so the swap
 * is invisible. These six loops were re-rendered from padded anchors
 * after their posters were cut (the homage idles, Legacy, the Stormglass
 * Ibex), so the loop frames its creature smaller than the poster does.
 * While their loop loads, the poster is drawn at the LOOP's framing:
 * scaled by `scale` about the box's top-left corner, then shifted by x/y
 * percent of the box - measured by registering each poster onto its
 * loop's first frame in a square contain-fit box (all six loops are
 * square, so the box is too). Mr. Khool's poster already matches. The
 * poster is not the loop's first POSE for these six (an orb's glow, a
 * flame mane, an arm), so a few px of the silhouette still shift at the
 * swap - about 2.5% of the box at most (the Noble One, the Ibex).
 * `rev` is the BASE_REV_OVERRIDES art each frame was measured on: re-render
 * one of these loops or posters in place (and bump its rev) and run-sync
 * fails until the frame is measured again. */
export const IDLE_POSTER_FRAMES: Record<string, { scale: number, x: number, y: number, rev: number }> = {
  aid: { scale: 0.6447, x: 10.57, y: 24.03, rev: 2 },
  lanfeust: { scale: 0.6452, x: 13.56, y: 20.5, rev: 2 },
  legacy: { scale: 0.8797, x: 9.8, y: 6.31, rev: 2 },
  robodude: { scale: 0.841, x: 3.41, y: 8.14, rev: 2 },
  'sparkvein-hare': { scale: 0.9094, x: 4.43, y: 6.94, rev: 2 },
  'the-noble-one': { scale: 0.9962, x: 0.73, y: -0.02, rev: 2 },
}

export function creatureShowcase(defId: string): string | null {
  const art = CREATURE_SHOWCASE[defId]
  return art ? assetUrl(diet(art)) : null
}

/**
 * 🎭 Mission-resolve states (v1.35, per Javi): victory and defeat loops
 * shown on the resolution screen. Since v1.64 EVERY creature ships both
 * states (Javi approved the full batch), so the maps derive from
 * CREATURE_ART instead of hand-listing 68 lines.
 */
/**
 * 🕐 v3.54 held twelve creatures out of the maps below while their state
 * loops rendered; v3.57 emptied that list — every creature in the pool
 * now ships all four states, so the maps derive from CREATURE_ART again.
 * If a future creature lands stills-first, put its id back here.
 */
const STATE_PENDING = new Set<string>([])

const animated = () => Object.keys(CREATURE_ART).filter(id => !STATE_PENDING.has(id))

// 🎭 m=1 (v5.36): 33 victory + 2 defeat loops were edge-masked IN PLACE
// in v5.34 (clipped silhouettes now dissolve at the canvas edge) and
// several were re-rendered outright in v5.36 — bust the day-long cache
// so the repairs actually show. Bump on any future in-place edit of the
// resolve states.
// (MASK_REV retired v5.69 — victory and defeat both carry per-id m=
// overrides now, defaulting to the mask era's m=1.)
// Per-id bumps for victory loops edited in place after m=1 shipped.
// steam-toad m=2 (v5.43): margin re-render review handful.
// 🐦 v5.69 bird batch: six victories re-rendered from padded anchors.
const VICTORY_REV_OVERRIDES: Record<string, number> = {
  'sparkvein-hare': 2, // v5.80 ibex rebirth
  // 🎗️ v6.57: the homage victories replaced their v6.56 stills in place.
  'mr-khool': 2,
  'aid': 2,
  'lanfeust': 2,
  'robodude': 2,
  'the-noble-one': 2,
  'steam-toad': 2,
  // v5.78: four victories re-rendered strict-profile (they turned to
  // face the camera mid-loop — Javi's report).
  'bubble-bat': 2,
  'coalpip': 2,
  'hoardwyrm': 2,
  'mosskit': 2,
  'volt-finch': 2,
  // 🎬 v7.59 (Javi's review): re-rendered on hydra from padded anchors - the
  // calf and pebblet were cut off, the sprite grew legs.
  'forge-calf': 2,
  'pebblet': 2,
  'soot-sprite': 2,
  'ashwing': 2,
  'cinderowl': 2,
  'solstice-phoenix': 2,
  'stormsinger': 2,
  'sunfledge': 2,
  // 🎬 v7.11: victories re-rendered from padded anchors after Javi's /anim review (cut-offs, tame loops).
  'deepcurrent-oracle': 2,
  'kilnwyrm': 2,
  'legacy': 2,
  'marshfang': 2,
  'mudfin': 2,
  'ripwing': 2,
  'root-toad': 2,
  'sable-corsair': 2,
  'storm-kit': 2,
  'stormforge-smith': 2,
  'tidemother-leviathan': 2,
  'thistlemote': 2,
  'mirricrow': 2,
  'leviathan-calf': 2,
  'springtail': 3, // v7.25: new source
  // v7.13: tail was off the left edge; re-rendered off a padded anchor. 🌱 v7.76 (per Javi: "aeon's victory
  // animation does something weird where it zooms in"): the v7.13 take pushed in 1.6-1.9x - re-rendered on hydra
  // at true size (it roars as its seed flares; no rear-up, the language that made the camera push in).
  'aeon-sprout': 3,
  'cinder-moth': 2, // 🦋 v7.75: wing tips ran off the tile - a soft edge mask instead of a straight cut
  'pepper-sprout': 2, // 🌶️ v7.76: new source
}
export const CREATURE_VICTORY: Record<string, string> = Object.fromEntries(
  animated().map(id => [id, `/creatures/${id}-victory.webp?m=${VICTORY_REV_OVERRIDES[id] ?? 1}&${DIET}`]),
)

// 🧊 ?v=2 (v4.15): defeat files were loop-patched IN PLACE to freeze on
// their last frame — bust the day-long cache so the fix actually shows.
// 🐦 v5.69: per-id bumps for defeats re-rendered after m=1 shipped.
const DEFEAT_REV_OVERRIDES: Record<string, number> = {
  'sparkvein-hare': 2, // v5.80 ibex rebirth
  // 🎗️ v6.57: the homage defeats replaced their v6.56 stills in place.
  'mr-khool': 2,
  'aid': 2,
  'lanfeust': 2,
  'robodude': 2,
  'the-noble-one': 2,
  'solstice-phoenix': 2,
  'stormsinger': 2,
  'springtail': 2, // v7.25: new source
  // 🎬 v7.59 (Javi's review): the four birds and the ossuary were cut off,
  // the pup and the mouse did not look beaten - all re-rendered on hydra.
  'ashwing': 2,
  'cinderowl': 2,
  'sunfledge': 2,
  'volt-finch': 2,
  'pyrelord-ossuary': 2,
  'pebble-pup': 2,
  'sparkmouse': 2,
  'cinder-moth': 2, // 🦋 v7.75: soft edge mask (wing tips ran off the tile)
  'pepper-sprout': 2, // 🌶️ v7.76: new source
}
export const CREATURE_DEFEAT: Record<string, string> = Object.fromEntries(
  animated().map(id => [id, `/creatures/${id}-defeat.webp?v=2&m=${DEFEAT_REV_OVERRIDES[id] ?? 1}&${DIET}`]),
)

/** 🏃 Run cycles (v2.06): every creature ships one — traveling parties
 * play these on the field, so the map derives from CREATURE_ART like
 * victory/defeat above.
 *
 * 🔄 RUN_REV (v3.92): cache-buster for the facing audit. 56 of these
 * files changed CONTENT under unchanged URLs across v3.81–v3.92, and
 * assets ship with `max-age=86400` — so a browser that had seen the old
 * file replayed the backwards version for up to a day. Javi verified
 * /anim and reported nine already-fixed creatures as still backwards:
 * every one was his browser's day-old copy. Bump this whenever a
 * delivered run cycle is edited in place. */
// r=3: the v3.94 asset diet — every run cycle re-encoded at 256px.
// r=4: eight facing corrections per Javi's in-game verification (v4.00) —
// six of them reverts of my own v3.87/88 misreads.
// r=5: the v5.34 edge-mask batch (25 run cycles) + the v5.36 fresh
// renders for the ten Javi flagged as needing new animations.
const RUN_REV = 5
// Per-id bumps for loops edited in place AFTER their family rev shipped
// — busts just that file instead of refetching all 55.
// bogmaw r=6 (v5.37): the mud-free render landed one release behind r=5.
// r=6 batch (v5.43): margin re-render REVIEW handful — whole bodies in
// frame at last (the clip-repair program's first real installs).
// r=6 bird batch (v5.69): all seven bird runs re-rendered from padded
// anchors — whole wingspans in frame at last.
const RUN_REV_OVERRIDES: Record<string, number> = {
  'springtail': 7, // v7.25: new source, hydra gallop
  'sparkvein-hare': 6, // v5.80 ibex rebirth
  // 🎗️ v6.57: the homage run cycles replaced their v6.56 stills in place.
  'mr-khool': 6,
  'aid': 6,
  'lanfeust': 6,
  'robodude': 6,
  'the-noble-one': 6,
  // v5.78: barrow-knight's "run" was a spellcast — a real run cycle at
  // last, from the run_sword_shield motion preset.
  'barrow-knight': 6,
  'bogmaw': 6,
  'mosskit': 6,
  'coalpip': 6,
  'kilnwyrm': 6,
  'glacier-warden': 6,
  'aeon-sprout': 6,
  'volt-finch': 6,
  'ashwing': 6,
  'cinderowl': 6,
  'ripwing': 6,
  'solstice-phoenix': 6,
  'stormsinger': 6,
  'sunfledge': 6,
  'cinder-moth': 6, // 🦋 v7.75: soft edge mask on all four sides (the wings ran off the tile)
  'pepper-sprout': 6, // 🌶️ v7.76: new source, a run on two legs
}
export const CREATURE_RUN: Record<string, string> = Object.fromEntries(
  animated().map(id => [id, `/creatures/${id}-running.webp?r=${RUN_REV_OVERRIDES[id] ?? RUN_REV}`]),
)

export function creatureRun(defId: string): string | null {
  const url = CREATURE_RUN[defId]
  return url ? assetUrl(url) : null
}

export function creatureVictory(defId: string): string | null {
  const url = CREATURE_VICTORY[defId]
  return url ? assetUrl(url) : null
}

export function creatureDefeat(defId: string): string | null {
  const url = CREATURE_DEFEAT[defId]
  return url ? assetUrl(url) : null
}

/** 🏅 v4.18 (Javi's art): puzzle trials wear their own painted crests —
 * keyed from art/UI/badges. Embers and Tides await their renders and
 * keep their generic fallbacks meanwhile. Hoisted here in v4.43 (per
 * Javi) so the wrap-up reveal shows the same crest as the board. */
export const TRIAL_BADGES: Record<string, string> = {
  'Trial of Whispers': '/ui/badges/trial-of-whispers.webp',
  'Trial of Echoes': '/ui/badges/trial-of-echoes.webp',
  'Trial of Mirrors': '/ui/badges/trial-of-mirrors.webp',
  'Trial of Shadows': '/ui/badges/trial-of-shadows.webp',
}

/** startsWith, not equality — surge trials suffix the name (⚡ etc.). */
export function trialBadgeForName(name: string): string | null {
  const hit = Object.keys(TRIAL_BADGES).find(n => name.startsWith(n))
  return hit ? TRIAL_BADGES[hit]! : null
}
